import { useMemo, useRef } from 'react'
import deepEqual from 'fast-deep-equal'
import { useGlobalState } from '@highlight/ipc/react'
import { CalendarEvent } from '@highlight/common'
import { getIsUserAttending } from '@shared/utils/connections/calendars'
import { differenceInCalendarDays } from 'date-fns'
import { isWithinNextMinutes, isRecentlyEnded } from '@shared/utils/DateUtils'

/**
 * Structural sharing over the calendar GlobalState array. Broadcasts cross IPC
 * via structured clone, so every update hands the renderer brand-new object
 * refs. Reconciling against the previous result keeps a value-equal event's
 * identity (the same approach React Query uses), so memoized MeetingCells and
 * downstream useMemo filters only re-run for events that actually changed.
 */
function useSharedCalendarEvents(): Array<CalendarEvent> {
  const [allEvents] = useGlobalState<Array<CalendarEvent>>('calendar.todaysEvents', [])
  const prevRef = useRef<{ byId: Map<string, CalendarEvent>; array: Array<CalendarEvent> }>({
    byId: new Map(),
    array: [],
  })

  return useMemo(() => {
    const prev = prevRef.current
    const nextById = new Map<string, CalendarEvent>()
    let unchanged = prev.array.length === allEvents.length

    const reconciled = allEvents.map((event, index) => {
      const cached = prev.byId.get(event.id)
      const resolved = cached && deepEqual(cached, event) ? cached : event
      nextById.set(event.id, resolved)
      if (unchanged && prev.array[index] !== resolved) unchanged = false
      return resolved
    })

    const result = unchanged ? prev.array : reconciled
    prevRef.current = { byId: nextById, array: result }
    return result
  }, [allEvents])
}

/**
 * Returns today's valid calendar events for upcoming meetings display
 * isInitialLoading is true only on first load, not on refetches
 */
export function useTodaysCalendarEvents() {
  return useCalendarEventsOnDay(0)
}

/** `useTodaysCalendarEvents` for the day `dayOffset` days from today; negative is the past. */
export function useCalendarEventsOnDay(dayOffset: number) {
  const allEvents = useSharedCalendarEvents()
  const [isLoading] = useGlobalState<boolean>('calendar.todaysEventsLoading', false)
  const hasLoadedOnce = useRef(false)

  const todaysEvents = useMemo(() => {
    const now = new Date()
    return allEvents.filter((event) => {
      if (!event.start || !(event.start instanceof Date) || isNaN(event.start.getTime())) {
        return false
      }
      if (event.isAllDay) return false
      if (!getIsUserAttending(event)) return false
      return differenceInCalendarDays(event.start, now) === dayOffset
    })
  }, [allEvents, dayOffset])

  // Track if we've loaded data at least once
  if (!isLoading && allEvents.length > 0) {
    hasLoadedOnce.current = true
  }

  // Only show loading on initial load, not refetches
  const isInitialLoading = isLoading && !hasLoadedOnce.current
  const isFetching = isLoading && hasLoadedOnce.current

  return { data: todaysEvents, isLoading: isInitialLoading, isFetching }
}

/**
 * Returns calendar events for the next week
 * isInitialLoading is true only on first load, not on refetches
 */
export function useNextWeekCalendarEvents() {
  const allEvents = useSharedCalendarEvents()
  const [isLoading] = useGlobalState<boolean>('calendar.todaysEventsLoading', false)
  const hasLoadedOnce = useRef(false)

  const nextWeekEvents = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const oneWeekFromNow = new Date(today)
    oneWeekFromNow.setDate(today.getDate() + 7)
    oneWeekFromNow.setHours(0, 0, 0, 0)

    return allEvents.filter((event) => {
      if (!event.start || !(event.start instanceof Date) || isNaN(event.start.getTime())) {
        return false
      }
      if (event.isAllDay) return false
      if (!getIsUserAttending(event)) return false

      const eventDate = new Date(event.start)
      return eventDate >= today && eventDate < oneWeekFromNow
    })
  }, [allEvents])

  // Track if we've loaded data at least once
  if (!isLoading && allEvents.length > 0) {
    hasLoadedOnce.current = true
  }

  // Only show loading on initial load, not refetches
  const isInitialLoading = isLoading && !hasLoadedOnce.current
  const isFetching = isLoading && hasLoadedOnce.current

  return { data: nextWeekEvents, isLoading: isInitialLoading, isFetching }
}

/**
 * Filters calendar events to show upcoming/current meetings
 */
export function useFilteredUpcomingMeetings(calendarEvents: Array<CalendarEvent> | undefined) {
  return useMemo(() => {
    if (!calendarEvents) return []

    const now = new Date()
    const activeOrRecentlyEnded = calendarEvents.filter((event) => {
      const eventEnd = event.end || event.start
      return eventEnd > now || isRecentlyEnded(eventEnd)
    })

    const currentAndSoon = activeOrRecentlyEnded
      .filter((meeting) => {
        const isHappeningNow = isWithinNextMinutes(meeting.start, 1, meeting.end)
        const startingSoon = isWithinNextMinutes(meeting.start, 60, meeting.end)
        const recentlyEnded = isRecentlyEnded(meeting.end)
        return isHappeningNow || startingSoon || recentlyEnded
      })
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())

    const firstAfterHour = activeOrRecentlyEnded
      .filter((meeting) => !isWithinNextMinutes(meeting.start, 60) && !isWithinNextMinutes(meeting.start, 1))
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())[0]

    if (firstAfterHour) {
      return [...currentAndSoon, firstAfterHour]
    }

    return currentAndSoon
  }, [calendarEvents])
}
